(function () {
  const isLocalPreview = () => ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)
    && ['http:', 'https:'].includes(window.location.protocol);
  const DEFAULT_CONFIG = {
    baseUrl: 'https://api2.poptex.com',
    timeoutMs: 30000,
    storageKey: 'poptex.anonymous_id',
    storefrontTokenKey: 'poptex.storefront_token',
    customerTokenKey: 'poptex.customer_token',
    directCustomerTokenPath: '/api/storefront/direct-customer-token'
  };

  const ROUTES = {
    health: () => '/api/health',
    storefrontSessionToken: () => '/api/storefront/session-token',
    caseUploadToken: () => '/api/storefront/case-upload-token',
    scenes: () => '/api/scenes',
    scene: (id) => `/api/scenes/${encodeURIComponent(id)}`,
    sceneEvents: (id) => `/api/scenes/${encodeURIComponent(id)}/events`,
    event: (id) => `/api/events/${encodeURIComponent(id)}`,
    eventTemplates: (id) => `/api/events/${encodeURIComponent(id)}/templates`,
    templates: () => '/api/templates',
    template: (id) => `/api/templates/${encodeURIComponent(id)}`,
    referencePresets: () => '/api/reference-presets',
    productSeries: () => '/api/product-series',
    productSeriesDetail: (id) => `/api/product-series/${encodeURIComponent(id)}`,
    productSeriesPatterns: (id) => `/api/product-series/${encodeURIComponent(id)}/patterns`,
    cases: () => '/api/cases',
    case: (id) => `/api/cases/${encodeURIComponent(id)}`
  };

  class PoptexApiError extends Error {
    constructor(message, details) {
      super(message);
      this.name = 'PoptexApiError';
      const errorDetails = details || {};
      this.status = errorDetails.status;
      this.payload = errorDetails.payload;
      this.url = errorDetails.url;
      this.method = errorDetails.method;
      this.cause = errorDetails.cause;
    }
  }

  const normalizeBaseUrl = (value) => String(value || DEFAULT_CONFIG.baseUrl).replace(/\/+$/, '');

  const joinUrl = (baseUrl, path, query) => {
    const url = new URL(`${normalizeBaseUrl(baseUrl)}${path}`);
    Object.entries(query || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, value);
      }
    });
    return url.toString();
  };

  const safeJson = async (response) => {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (_error) {
      return text;
    }
  };

  const getAnonymousId = (storageKey) => {
    const key = storageKey || DEFAULT_CONFIG.storageKey;
    try {
      const existing = window.localStorage.getItem(key);
      if (existing) return existing;
      const id = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `anon-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      window.localStorage.setItem(key, id);
      return id;
    } catch (_error) {
      return `anon-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  };

  const createClient = (inputConfig) => {
    const config = Object.assign({}, DEFAULT_CONFIG, inputConfig || {});
    if (isLocalPreview()) {
      for (const key of ['storageKey', 'storefrontTokenKey', 'customerTokenKey']) config[key] += '.local-preview.v1';
    }
    const baseUrl = normalizeBaseUrl(config.baseUrl);
    let storefrontTokenRequest = null;

    const request = async (path, options) => {
      const requestOptions = options || {};
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), requestOptions.timeoutMs || config.timeoutMs);
      const cancel = () => controller.abort(requestOptions.signal.reason);
      requestOptions.signal?.addEventListener('abort', cancel, {once:true});
      if (requestOptions.signal?.aborted) cancel();
      const headers = new Headers(requestOptions.headers || {});
      const hasFormData = requestOptions.body instanceof FormData;

      if (!hasFormData && requestOptions.body !== undefined && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }

      if (requestOptions.authToken) {
        headers.set('Authorization', `Bearer ${requestOptions.authToken}`);
      }

      const url = joinUrl(baseUrl, path, requestOptions.query);

      try {
        const response = await fetch(url, {
          method: requestOptions.method || 'GET',
          headers,
          body: hasFormData ? requestOptions.body : requestOptions.body === undefined ? undefined : JSON.stringify(requestOptions.body),
          credentials: requestOptions.credentials || 'omit',
          signal: controller.signal
        });
        const payload = await safeJson(response);

        if (!response.ok) {
          const message = payload && payload.message
            ? Array.isArray(payload.message) ? payload.message.join(', ') : payload.message
            : `Request failed with status ${response.status}`;
          throw new PoptexApiError(message, {
            status: response.status,
            payload,
            url: response.url,
            method: requestOptions.method || 'GET'
          });
        }

        return payload;
      } catch (error) {
        if (error && error.name === 'PoptexApiError') {
          throw error;
        }

        const isAbort = error && error.name === 'AbortError';
        throw new PoptexApiError(
          isAbort
            ? `Request timed out after ${requestOptions.timeoutMs || config.timeoutMs}ms`
            : 'Network error while requesting POPTEX API',
          {
            status: 0,
            payload: null,
            url,
            method: requestOptions.method || 'GET',
            cause: error
          }
        );
      } finally {
        window.clearTimeout(timeout);
        requestOptions.signal?.removeEventListener('abort', cancel);
      }
    };

    const getStoredStorefrontToken = () => {
      try {
        return window.sessionStorage.getItem(config.storefrontTokenKey);
      } catch (_error) {
        return null;
      }
    };

    const setStoredStorefrontToken = (token) => {
      try {
        if (token) window.sessionStorage.setItem(config.storefrontTokenKey, token);
      } catch (_error) {
        // Session storage can be unavailable in strict privacy modes.
      }
    };

    const clearStoredStorefrontToken = () => {
      try {
        window.sessionStorage.removeItem(config.storefrontTokenKey);
      } catch (_error) {
        // Session storage can be unavailable in strict privacy modes.
      }
    };

    const getClientAnonymousId = () => getAnonymousId(config.storageKey);
    const getStoredCustomerToken = () => {
      try {
        return window.sessionStorage.getItem(config.customerTokenKey);
      } catch (_error) {
        return null;
      }
    };

    const setStoredCustomerToken = (token) => {
      try {
        if (token) window.sessionStorage.setItem(config.customerTokenKey, token);
      } catch (_error) {
        // Session storage can be unavailable in strict privacy modes.
      }
    };

    const clearStoredCustomerToken = () => {
      try {
        window.sessionStorage.removeItem(config.customerTokenKey);
      } catch (_error) {
        // Session storage can be unavailable in strict privacy modes.
      }
    };

    const withAnonymousHeader = (headers, anonymousId) => Object.assign(
      {},
      headers || {},
      { 'X-POPTEX-Anonymous-Id': anonymousId }
    );

    const createStorefrontSessionToken = () => {
      const anonymousId = getClientAnonymousId();
      return request(ROUTES.storefrontSessionToken(), {
        method: 'POST',
        headers: withAnonymousHeader(null, anonymousId),
        body: { anonymousId }
      }).then((response) => {
        const token = response && response.storefrontToken;
        if (!token) {
          throw new PoptexApiError('Storefront session token is missing from response', {
            status: 0,
            payload: response,
            url: joinUrl(baseUrl, ROUTES.storefrontSessionToken()),
            method: 'POST'
          });
        }
        if (token) setStoredStorefrontToken(token);
        return token;
      });
    };

    const ensureStorefrontToken = (forceRefresh) => {
      if (!forceRefresh) {
        const existing = getStoredStorefrontToken();
        if (existing) return Promise.resolve(existing);
      }

      if (!storefrontTokenRequest) {
        if (forceRefresh) clearStoredStorefrontToken();
        storefrontTokenRequest = createStorefrontSessionToken()
          .finally(() => {
            storefrontTokenRequest = null;
          });
      }

      return storefrontTokenRequest;
    };

    const publicRequest = (path, options, retryOnUnauthorized) => {
      const requestOptions = options || {};
      const shouldRetry = retryOnUnauthorized !== false;
      const anonymousId = getClientAnonymousId();

      return ensureStorefrontToken(false)
        .then((storefrontToken) => {
          const headers = withAnonymousHeader(requestOptions.headers, anonymousId);
          headers['X-POPTEX-Storefront-Token'] = storefrontToken;
          if (requestOptions.uploadToken) {
            headers['X-POPTEX-Upload-Token'] = requestOptions.uploadToken;
          }
          const customerToken = getStoredCustomerToken();
          if (customerToken) {
            headers['X-POPTEX-Customer-Token'] = customerToken;
          }

          return request(path, Object.assign({}, requestOptions, { headers }));
        })
        .catch((error) => {
          if (!shouldRetry || !error || error.status !== 401) {
            throw error;
          }
          if (getStoredCustomerToken()) {
            clearStoredCustomerToken();
          }

          return ensureStorefrontToken(true).then((storefrontToken) => {
            const headers = withAnonymousHeader(requestOptions.headers, anonymousId);
            headers['X-POPTEX-Storefront-Token'] = storefrontToken;
            if (requestOptions.uploadToken) {
              headers['X-POPTEX-Upload-Token'] = requestOptions.uploadToken;
            }
            const customerToken = getStoredCustomerToken();
            if (customerToken) {
              headers['X-POPTEX-Customer-Token'] = customerToken;
            }

            return request(path, Object.assign({}, requestOptions, { headers }));
          });
        });
    };

    const getShopifyCustomer = () => {
      if (isLocalPreview()) {
        return { id: 'local-preview:' + getClientAnonymousId().slice(0, 64), name: 'Local preview' };
      }
      const customer = window.POPTEX_CUSTOMER;
      if (!customer || !customer.id) return null;
      const firstName = customer.firstName || customer.first_name || '';
      const lastName = customer.lastName || customer.last_name || '';
      const name = [firstName, lastName].filter(Boolean).join(' ') || customer.name || '';
      return {
        id: String(customer.id),
        email: customer.email || undefined,
        name: name || undefined,
        phone: customer.phone || undefined
      };
    };

    const refreshCustomerSession = () => {
      const shopifyCustomer = getShopifyCustomer();
      if (!shopifyCustomer) {
        clearStoredCustomerToken();
        return Promise.resolve({ authenticated: false });
      }

      const anonymousId = getClientAnonymousId();
      return publicRequest(config.directCustomerTokenPath, {
        method: 'POST',
        body: {
          anonymousId,
          shopifyCustomerId: shopifyCustomer.id,
          customerEmail: shopifyCustomer.email,
          customerName: shopifyCustomer.name,
          customerPhone: shopifyCustomer.phone
        }
      })
        .then((response) => {
          if (response && response.authenticated && response.customerToken) {
            setStoredCustomerToken(response.customerToken);
            return response;
          }
          clearStoredCustomerToken();
          return Object.assign({ authenticated: false }, response || {});
        })
        .catch((error) => {
          clearStoredCustomerToken();
          if (error && (error.status === 401 || error.status === 404)) {
            return { authenticated: false };
          }
          throw error;
        });
    };

    const linkCustomer = (input) => {
      const customerToken = getStoredCustomerToken();
      if (!customerToken) return Promise.resolve(null);
      const anonymousId = getClientAnonymousId();
      return publicRequest(ROUTES.cases() + '/link-customer', {
        method: 'POST',
        headers: { 'X-POPTEX-Customer-Token': customerToken },
        body: Object.assign({ anonymousId }, input || {})
      });
    };

    const createCaseUploadToken = (context) => {
      const anonymousId = getClientAnonymousId();
      const selection = typeof context === 'string' ? { templateId: context } : (context || {});
      return publicRequest(ROUTES.caseUploadToken(), {
        method: 'POST',
        body: Object.assign({ anonymousId }, selection)
      }).then((response) => response && response.uploadToken);
    };

    const registryRequest = (method, args, fallbackRequest, options) => {
      const registry = window.PoptexContentRegistry;
      const requestOptions = options || {};
      if (!registry || typeof registry[method] !== 'function') {
        return fallbackRequest();
      }

      if (typeof registry.refresh === 'function') {
        registry.refresh();
      } else if (typeof registry.sync === 'function') {
        window.setTimeout(() => {
          registry.sync().catch(() => {});
        }, 0);
      }

      return registry[method].apply(registry, args || [])
        .then((result) => {
          const hasUsableResult = Array.isArray(result) ? result.length > 0 : Boolean(result);
          return hasUsableResult || requestOptions.allowEmpty ? result : fallbackRequest();
        })
        .catch((error) => {
          console.warn(`POPTEX metadata ${method} failed, falling back to API:`, error);
          return fallbackRequest();
        });
    };

    const normalizeIdentity = (value) => String(value || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const sceneAliases = {
      corporate: 'matrix-enterprise',
      'corporate-solutions': 'matrix-enterprise',
      'corporate-gifts': 'matrix-enterprise',
      'sports-commemorations': 'matrix-sports-team',
      sports: 'matrix-sports-team',
      'graduation-keepsakes': 'matrix-school',
      graduation: 'matrix-school',
      school: 'matrix-school',
      schools: 'matrix-school',
      'souvenir-shops': 'matrix-scenic-cultural',
      souvenir: 'matrix-scenic-cultural',
      tourism: 'matrix-scenic-cultural',
      'music-festivals': 'matrix-event-celebration',
      music: 'matrix-event-celebration',
      events: 'matrix-event-celebration'
    };

    const imageFrom = (record, roles) => {
      const images = record && record.images || record && record.previewImages || {};
      const roleList = roles || ['banner', 'thumbnail', 'mainEffect', 'product', 'usageScene', 'brandMark', 'giftBox'];
      for (const role of roleList) {
        if (images && images[role]) return images[role];
      }
      return record && (record.previewUrl || record.image || record.url) || '';
    };

    const bySortAndName = (a, b) => (
      (Number(b && b.sortOrder) || 0) - (Number(a && a.sortOrder) || 0)
      || String(a && a.name || '').localeCompare(String(b && b.name || ''))
    );

    const resolveSceneSlug = (handleOrSlug) => {
      const key = normalizeIdentity(handleOrSlug);
      return sceneAliases[key] || key;
    };

    const buildTemplateCatalog = () => Promise.all([
      registryRequest('listScenes', [], () => publicRequest(ROUTES.scenes()), { allowEmpty: true }),
      registryRequest('listEvents', [], () => Promise.resolve([]), { allowEmpty: true }),
      registryRequest('listTemplates', [], () => publicRequest(ROUTES.templates()), { allowEmpty: true })
    ]).then(([scenes, events, templates]) => {
      const sceneRecords = Array.isArray(scenes) ? scenes.slice().sort(bySortAndName) : [];
      const eventRecords = Array.isArray(events) ? events.slice().sort(bySortAndName) : [];
      const templateRecords = Array.isArray(templates) ? templates.slice().sort(bySortAndName) : [];
      return {
        scenes: sceneRecords,
        events: eventRecords,
        templates: templateRecords,
        eventsByScene: eventRecords.reduce((map, event) => {
          const sceneId = event && event.sceneId;
          if (!sceneId) return map;
          if (!map[sceneId]) map[sceneId] = [];
          map[sceneId].push(event);
          return map;
        }, {}),
        templatesByEvent: templateRecords.reduce((map, template) => {
          const eventId = template && template.eventId;
          if (!eventId) return map;
          if (!map[eventId]) map[eventId] = [];
          map[eventId].push(template);
          return map;
        }, {})
      };
    });

    const getTemplateCatalog = () => buildTemplateCatalog();

    const getScenePage = (handleOrSlug) => buildTemplateCatalog().then((catalog) => {
      const wanted = resolveSceneSlug(handleOrSlug);
      const scene = catalog.scenes.find((item) => {
        const identities = [item.id, item.slug, item.handle, normalizeIdentity(item.name)].map(normalizeIdentity);
        return identities.includes(wanted);
      }) || null;
      const events = scene ? catalog.events.filter((event) => event.sceneId === scene.id) : [];
      const templates = events.flatMap((event) => catalog.templatesByEvent[event.id] || []);
      return {
        handle: handleOrSlug,
        resolvedSlug: wanted,
        scene,
        events,
        templates,
        heroImage: imageFrom(scene, ['thumbnail', 'banner']),
        eventCards: events.map((event) => ({
          id: event.id,
          slug: event.slug,
          name: event.name,
          description: event.displayIntro || event.description || '',
          sortOrder: Number(event.sortOrder) || 0,
          image: imageFrom(event, ['thumbnail', 'banner']),
          templates: catalog.templatesByEvent[event.id] || []
        }))
      };
    });

    let patternColorCache = null;
    let patternColorRequest = null;
    let colorRevision = 0;
    const parseColorCatalog = (families, patterns) => {
      const codes = ['BLACK_GRAY','WHITE_BEIGE','BROWN','RED_PINK','ORANGE_YELLOW','GREEN','BLUE','PURPLE'];
      if (!Array.isArray(families) || families.length !== 8 || !Array.isArray(patterns)) throw new Error('Invalid color catalog');
      const orders = [];
      families.forEach((family, index) => {
        if (family.code !== codes[index] || family.samples?.length !== 1 || !/^#[0-9a-f]{6}$/i.test(family.samples[0]) || !family.classificationSamples?.length) throw new Error('Invalid color family');
        family.classificationSamples.forEach(ref => {
          if (!/^#[0-9a-f]{6}$/i.test(ref.hex) || !Number.isInteger(ref.order)) throw new Error('Invalid classification reference');
          orders.push(ref.order);
        });
      });
      if (orders.sort((a,b)=>a-b).join(',') !== '0,1,2,3,4,5,6,7,8,9,10,11') throw new Error('Invalid classification order');
      const patternsById = Object.create(null);
      patterns.forEach(pattern => {
        if (!Array.isArray(pattern.colors) || pattern.colors.some(c => c?.family != null && !codes.includes(c.family))) throw new Error('Incompatible pattern colors');
        patternsById[pattern.id] = { colors: pattern.colors };
      });
      return { families, patternsById };
    };
    window.addEventListener('poptex:metadata-updated', () => {
      colorRevision += 1;
      patternColorCache = null;
      patternColorRequest = null;
    });
    const getPatternColorCatalog = () => {
      if (patternColorCache && Date.now() - patternColorCache.at < 300000) return Promise.resolve(patternColorCache.value);
      if (patternColorRequest) return patternColorRequest;
      const revision = colorRevision;
      const pending = (async () => {
        let value;
        try {
          await window.PoptexContentRegistry?.init();
          const snapshot = window.PoptexContentRegistry?.getSnapshot();
          if (snapshot?.schemaVersion !== '2.2.0') throw new Error('Old color snapshot');
          value = parseColorCatalog(snapshot.colorFamilies, snapshot.patterns);
        } catch (_) {
          const [definitions, patterns] = await Promise.all([
            publicRequest('/api/patterns/color-families'), publicRequest('/api/patterns')
          ]);
          value = parseColorCatalog(definitions.data, patterns.data);
        }
        if (revision !== colorRevision) return getPatternColorCatalog();
        patternColorCache = { at: Date.now(), value };
        return value;
      })().finally(() => { if (patternColorRequest === pending) patternColorRequest = null; });
      patternColorRequest = pending;
      return pending;
    };

    const getDesignCatalog = () => Promise.all([publicRequest('/api/product-series').then(r=>r.data),publicRequest('/api/patterns').then(r=>r.data),publicRequest(ROUTES.referencePresets()).then(r=>Array.isArray(r)?r:r.data)]).then(([productSeries,patterns,referencePresets])=>({productSeries,patterns,referencePresets:referencePresets.filter(p=>['BRAND_MARK','PACKAGING'].includes(p.type))}));

    return {
      config: Object.assign({}, config, { baseUrl }),
      routes: ROUTES,
      request,
      getAnonymousId: getClientAnonymousId,
      health: () => request(ROUTES.health()),
      storefront: {
        hasCustomerIdentity: () => Boolean(getShopifyCustomer()),
        ensureSessionToken: ensureStorefrontToken,
        createCaseUploadToken,
        refreshCustomerSession,
        getCustomerToken: getStoredCustomerToken,
        clearCustomerToken: clearStoredCustomerToken
      },
      cms: {
        getPatternColorCatalog,
        listPatterns: (query) => registryRequest('listPatterns', [query], () => publicRequest('/api/patterns', {query}).then(r=>r.data), {allowEmpty:true}),
        getPattern: (id) => registryRequest('getPattern', [id], () => publicRequest('/api/patterns/'+encodeURIComponent(id)).then(r=>r.data)),
        refreshV2Options: (styleId) => Promise.all([publicRequest('/api/product-series/'+encodeURIComponent(styleId)).then(r=>r.data), publicRequest('/api/patterns',{query:{productSeriesId:styleId}}).then(r=>r.data), publicRequest(ROUTES.referencePresets()).then(r=>Array.isArray(r)?r:r.data)]).then(async ([style,patterns,referencePresets])=>({style,patterns,referencePresets:referencePresets.filter(p=>['BRAND_MARK','PACKAGING'].includes(p.type)),group:style.groupId?await publicRequest('/api/product-series-groups/'+encodeURIComponent(style.groupId)).then(r=>r.data):null})),
        listScenes: () => registryRequest('listScenes', [], () => publicRequest(ROUTES.scenes())),
        getScene: (id) => registryRequest('getScene', [id], () => publicRequest(ROUTES.scene(id))),
        listSceneEvents: (id) => registryRequest('listSceneEvents', [id], () => publicRequest(ROUTES.sceneEvents(id))),
        getEvent: (id) => registryRequest('getEvent', [id], () => publicRequest(ROUTES.event(id))),
        listEventTemplates: (id) => registryRequest('listEventTemplates', [id], () => publicRequest(ROUTES.eventTemplates(id))),
        listTemplates: (query) => registryRequest('listTemplates', [query], () => publicRequest(ROUTES.templates(), { query })),
        getTemplate: (id) => registryRequest('getTemplate', [id], () => publicRequest(ROUTES.template(id))),
        listReferencePresets: (query) => registryRequest('listReferencePresets', [query], () => publicRequest(ROUTES.referencePresets(), { query })),
        listProductSeries: (query) => registryRequest('listProductSeries', [query], () => publicRequest(ROUTES.productSeries(), { query }).then((result) => result.data), { allowEmpty: true }),
        getProductSeries: (id) => registryRequest('getProductSeries', [id], () => publicRequest(ROUTES.productSeriesDetail(id)).then((result) => result.data)),
        listProductSeriesPatterns: (id, query) => registryRequest('listProductSeriesPatterns', [id, query], () => publicRequest(ROUTES.productSeriesPatterns(id), { query }).then((result) => result.data), { allowEmpty: true }),
        listAssets: () => registryRequest('listAssets', [], () => Promise.resolve([]), { allowEmpty: true }),
        getAsset: (id) => registryRequest('getAsset', [id], () => Promise.resolve(null), { allowEmpty: true }),
        getScenePage,
        getTemplateCatalog,
        getDesignCatalog
      },
      cases: {
        beginSubmission: (body, options = {}) => publicRequest(ROUTES.cases()+'/submissions', {
          method:'POST', body, uploadToken:options.uploadToken, signal:options.signal,
          headers:{'X-POPTEX-Client-Source':'SHOPIFY_THEME_DESIGN_STUDIO'}
        }, false),
        getSubmission: (id, options = {}) => publicRequest(ROUTES.cases()+'/submissions/'+encodeURIComponent(id), {signal:options.signal}),
        uploadSubmissionInput: (id, key, file, options = {}) => {
          const body = new FormData(); body.append('file',file,file.name || key+'.png');
          // A single file has its own transport budget; this is not the model deadline.
          return publicRequest(ROUTES.cases()+'/submissions/'+encodeURIComponent(id)+'/inputs/'+encodeURIComponent(key), {
            method:'POST', body, timeoutMs:120000, signal:options.signal
          }, false);
        },
        completeSubmission: (id, options = {}) => publicRequest(ROUTES.cases()+'/submissions/'+encodeURIComponent(id)+'/complete', {
          method:'POST', signal:options.signal
        }, false),
        retrySlot: (id,slot) => publicRequest(ROUTES.case(id)+'/preview-slots/'+encodeURIComponent(slot)+'/retry', {method:'POST',query:{anonymousId:getClientAnonymousId()}}),
        generate: (id) => publicRequest(ROUTES.case(id)+'/generations', {method:'POST',query:{anonymousId:getClientAnonymousId()}}),
        create: (body, options) => publicRequest(ROUTES.cases(), {
          method: 'POST',
          timeoutMs: 30000,
          headers: { 'X-POPTEX-Client-Source': 'SHOPIFY_THEME_DESIGN_STUDIO' },
          uploadToken: options && options.uploadToken,
          body
        }, false),
        list: (query) => publicRequest(ROUTES.cases(), {
          query: Object.assign({
            anonymousId: getStoredCustomerToken() ? undefined : getClientAnonymousId()
          }, query || {})
        }),
        get: (id) => publicRequest(ROUTES.case(id), {
          query: { anonymousId: getClientAnonymousId() }
        }),
        update: (id, body) => publicRequest(ROUTES.case(id), {
          method: 'PATCH',
          body: Object.assign({ anonymousId: getClientAnonymousId() }, body || {})
        }),
        linkCustomer
      }
    };
  };

  const getDefaultClient = () => {
    if (!window.PoptexApiClient) {
      window.PoptexApiClient = createClient(window.POPTEX_API_CONFIG || DEFAULT_CONFIG);
    }
    return window.PoptexApiClient;
  };

  window.PoptexApi = {
    routes: ROUTES,
    createClient,
    getDefaultClient,
    getAnonymousId,
    isLocalPreview,
    hasCustomerIdentity: () => getDefaultClient().storefront.hasCustomerIdentity(),
    ApiError: PoptexApiError
  };
})();
